import {
  docLabel,
  hasDrawingMatch,
  hasDrawings,
  matches,
  wbsLabel,
} from "./treeLabels";
import { isSoftwareWbs, treeGroupKey, wbsGroupNodeId } from "./wbsGroups";

export const SEARCH_DEBOUNCE_MS = 400;

// Open just enough to put each match on screen, and no more: the plant and
// the discipline folder above a matching WBS. Deliberately NOT expanded:
// - a plant whose own name matched, since the match is the plant row itself
//   and expanding it would unfurl every discipline and every WBS under it;
// - the matching WBS node, whose contents nobody asked to see.
// The tree is already filtered to matches, so anything worth opening is one
// click away.
export const buildAutoExpandIds = ({
  plants,
  query,
  wbsByPlant,
  docsByWbs,
  drawingsByWbs,
}) => {
  if (!query) return null;
  const ids = new Set();

  plants.forEach((plant) => {
    const wbsList = wbsByPlant[plant.id];
    if (!Array.isArray(wbsList)) return;

    wbsList.forEach((wbs) => {
      if (isSoftwareWbs(wbs) && !hasDrawings(wbs, drawingsByWbs)) return;
      const wbsMatches = matches(wbsLabel(wbs), query);
      const docs = docsByWbs[wbs.id];
      const hasDocMatch =
        Array.isArray(docs) &&
        docs.some((doc) => matches(docLabel(doc), query));

      if (
        wbsMatches ||
        hasDocMatch ||
        hasDrawingMatch(wbs, query, drawingsByWbs)
      ) {
        ids.add(`plant-${plant.id}`);
        // The type folder sits between plant and WBS, so it has to open
        // too or the match stays hidden inside it.
        ids.add(wbsGroupNodeId(plant.id, treeGroupKey(wbs)));
      }
    });
  });

  return Array.from(ids);
};

export const isPlantVisible = (
  plant,
  query,
  wbsByPlant,
  docsByWbs,
  drawingsByWbs,
) => {
  if (!query) return true;
  if (matches(plant.name || "", query)) return true;
  const wbsList = wbsByPlant[plant.id];
  if (!Array.isArray(wbsList)) return false;
  // Mirrors the PlantNode type filter: legacy / unrecognised types stay
  // hidden unless the WBS carries 2D drawings.
  return wbsList.some(
    (wbs) =>
      (!isSoftwareWbs(wbs) || hasDrawings(wbs, drawingsByWbs)) &&
      isWbsVisible(plant, wbs, query, docsByWbs, drawingsByWbs),
  );
};

export const isWbsVisible = (plant, wbs, query, docsByWbs, drawingsByWbs) => {
  if (!query) return true;
  if (matches(plant.name || "", query)) return true;
  if (matches(wbsLabel(wbs), query)) return true;
  if (hasDrawingMatch(wbs, query, drawingsByWbs)) return true;
  const docs = docsByWbs[wbs.id];
  return (
    Array.isArray(docs) && docs.some((doc) => matches(docLabel(doc), query))
  );
};

export const isDocVisible = (plant, wbs, doc, query) => {
  if (!query) return true;
  if (matches(plant.name || "", query)) return true;
  if (matches(wbsLabel(wbs), query)) return true;
  return matches(docLabel(doc), query);
};
